/**
 * App-level CSS imports — order matters for specificity.
 *
 * 1. Bootstrap 3.4.1 base styles
 * 2. APSUserExperienceAdvantage UX library styles
 * 3. App-level CSS (converted from main.less)
 *
 * This preserves pixel-perfect rendering by using the exact same
 * CSS cascade as the AngularJS app.
 */
import 'bootstrap/dist/css/bootstrap.min.css'
import 'UserExperience/advantage/dist/css/APSUserExperienceAdvantage.css'
import './app.css'

import { useEffect, type ReactNode } from 'react'
import { HashRouter, useLocation } from 'react-router-dom'
import { AuthProvider } from '@common/auth'
import { AbilityProvider } from '@common/abilities'
import { NotificationProvider } from '@common/notifications/NotificationContext'
import { ConfirmationProvider } from '@common/components/ConfirmationModal'
import { AppConfigProvider } from '@common/config/AppConfigContext'
import { ScrollToTop } from '@common/components/ScrollToTop'
import { ErrorBoundary } from '@common/components/ErrorBoundary'
import { errorReporting } from '@common/services/errorReporting'
import { AppRoutes } from './routes'
import { AppLayout } from './layout/AppLayout'

/** Catches unhandled promise rejections and sends them to Raygun */
function UnhandledRejectionHandler() {
  useEffect(() => {
    const handler = (event: PromiseRejectionEvent) => {
      const error = event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason))
      console.error('Unhandled promise rejection:', error)
      errorReporting.send(error)
    }
    window.addEventListener('unhandledrejection', handler)
    return () => window.removeEventListener('unhandledrejection', handler)
  }, [])
  return null
}

/** Resets the error boundary on every route change so a crash on one page doesn't lock out all others. */
function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation()
  return <ErrorBoundary key={location.pathname}>{children}</ErrorBoundary>
}

export function App() {
  return (
    <ErrorBoundary>
      <AppConfigProvider>
        <AuthProvider>
          <AbilityProvider>
            <NotificationProvider>
              <ConfirmationProvider>
                <HashRouter>
                  <UnhandledRejectionHandler />
                  <ScrollToTop />
                  <AppLayout>
                    <RouteErrorBoundary>
                      <AppRoutes />
                    </RouteErrorBoundary>
                  </AppLayout>
                </HashRouter>
              </ConfirmationProvider>
            </NotificationProvider>
          </AbilityProvider>
        </AuthProvider>
      </AppConfigProvider>
    </ErrorBoundary>
  )
}
